import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { 
  Search, Home, ChevronRight, 
  FolderOpen, Calendar, Clock, AlertCircle,
  Timer, FileText, ExternalLink
} from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";

interface AnnouncementItem {
  id: number;
  project_no: string;
  project_name: string;
  bidding_unit: string;
  publish_time: string;
  open_bid_time: string;
  registration_end_time: string;
  announcement_type: number;
}

type TabType = 'all' | 'ongoing' | 'ended';

export default function PurchaserBidsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  // 获取招标项目列表
  useEffect(() => {
    fetchAnnouncements();
  }, [activeTab, searchKeyword]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchKeyword) params.append('keyword', searchKeyword);
      if (activeTab !== 'all') params.append('status', activeTab);
      
      const resp = await fetch(`/api/purchaser/my-announcements?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then(r => r.json());
      
      if (resp.ok) {
        setAnnouncements(resp.data || []);
      }
    } catch (e) {
      console.error('获取招标列表失败:', e);
    } finally {
      setLoading(false);
    }
  };

  // 获取状态信息
  const getDeadlineStatus = (openBidTime: string) => {
    const now = new Date();
    const openDate = new Date(openBidTime);
    const diff = openDate.getTime() - now.getTime();
    
    if (diff <= 0) {
      return { status: 'ended', label: '已截止', color: '#ef4444' };
    } else if (diff <= 3 * 24 * 60 * 60 * 1000) {
      return { status: 'urgent', label: '即将开标', color: '#f59e0b' };
    }
    return { status: 'ongoing', label: '进行中', color: '#10b981' };
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0].replace(/-/g, '/');
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // 计算统计数据
  const stats = {
    all: announcements.length,
    ongoing: announcements.filter(a => new Date(a.open_bid_time) > new Date()).length,
    ended: announcements.filter(a => new Date(a.open_bid_time) <= new Date()).length
  };

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'all', label: '全部项目', count: stats.all },
    { key: 'ongoing', label: '进行中', count: stats.ongoing },
    { key: 'ended', label: '已截止', count: stats.ended }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header simple />
      
      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-80px',
          right: '-50px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div style={{
              width: isMobile ? '40px' : '56px',
              height: isMobile ? '40px' : '56px',
              borderRadius: '16px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FolderOpen style={{ width: isMobile ? '20px' : '28px', height: isMobile ? '20px' : '28px', color: 'white' }} />
            </div>
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              我的招标
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px' }}>
            管理您发布的招标项目，跟踪项目进度
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.color = '#7c3aed'}
              onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}
            >
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#7c3aed', fontWeight: 500 }}>我的招标</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        {/* 统计卡片 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: isMobile ? '12px' : '20px',
          marginBottom: isMobile ? '20px' : '32px'
        }}>
          {[
            { label: '全部项目', value: stats.all, icon: FolderOpen, color: '#7c3aed', bgColor: '#f5f3ff' },
            { label: '进行中', value: stats.ongoing, icon: Timer, color: '#10b981', bgColor: '#ecfdf5' },
            { label: '已截止', value: stats.ended, icon: AlertCircle, color: '#ef4444', bgColor: '#fef2f2' }
          ].map((stat, index) => (
            <div key={index} style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '16px' : '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '12px' : '20px'
            }}>
              <div style={{
                width: isMobile ? '40px' : '56px',
                height: isMobile ? '40px' : '56px',
                borderRadius: '14px',
                backgroundColor: stat.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <stat.icon style={{ width: isMobile ? '20px' : '28px', height: isMobile ? '20px' : '28px', color: stat.color }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 700, color: '#1f2937' }}>{stat.value}</div>
                <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280' }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* 列表卡片 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* Tab 和搜索栏 */}
          <div style={{
            padding: isMobile ? '16px' : '24px 32px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(to right, #fafbfc, white)',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? '12px' : '0'
          }}>
            {/* Tab */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', flexWrap: isMobile ? 'wrap' : 'nowrap', overflowX: isMobile ? 'auto' : 'visible' }}>
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: isMobile ? '8px 14px' : '10px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: activeTab === tab.key ? '#7c3aed' : '#f3f4f6',
                    color: activeTab === tab.key ? 'white' : '#6b7280',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {tab.label}
                  <span style={{
                    padding: isMobile ? '2px 6px' : '2px 8px',
                    backgroundColor: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
                    borderRadius: '10px',
                    fontSize: isMobile ? '11px' : '12px'
                  }}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            
            {/* 搜索框 */}
            <div style={{ position: 'relative', width: isMobile ? '100%' : '320px' }}>
              <input
                type="text"
                placeholder="搜索项目名称或编号..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={{
                  width: '100%',
                  height: isMobile ? '40px' : '44px',
                  paddingLeft: isMobile ? '40px' : '48px',
                  paddingRight: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: isMobile ? '13px' : '14px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#7c3aed';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(124,58,237,0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <Search style={{ 
                position: 'absolute', 
                left: isMobile ? '12px' : '16px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                width: isMobile ? '18px' : '20px', 
                height: isMobile ? '18px' : '20px', 
                color: '#9ca3af' 
              }} />
            </div>
          </div>

          {/* 项目列表 */}
          {loading ? (
            <div style={{ padding: isMobile ? '40px 20px' : '80px 0', textAlign: 'center' }}>
              <p style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>加载中...</p>
            </div>
          ) : announcements.length > 0 ? (
            <div>
              {announcements.map((item, index) => {
                const status = getDeadlineStatus(item.open_bid_time);
                
                return (
                  <div
                    key={item.id}
                    onClick={() => navigate(`/announcement-detail/1/${item.id}`)}
                    onMouseEnter={() => !isMobile && setHoveredRow(item.id)}
                    onMouseLeave={() => !isMobile && setHoveredRow(null)}
                    style={{
                      padding: isMobile ? '16px' : '24px 32px',
                      borderBottom: index < announcements.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: hoveredRow === item.id && !isMobile
                        ? 'linear-gradient(to right, #f5f3ff, transparent)' 
                        : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* 顶部：项目名称和状态 */}
                    <div style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? '6px' : '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: isMobile ? '3px 8px' : '4px 10px',
                          backgroundColor: status.color + '15',
                          color: status.color,
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: 600,
                          borderRadius: '6px'
                        }}>
                          {status.label}
                        </span>
                        <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#9ca3af', fontFamily: 'monospace' }}>
                          {item.project_no}
                        </span>
                      </div>
                      <h4 style={{ 
                        fontSize: isMobile ? '15px' : '17px', 
                        fontWeight: 600, 
                        color: hoveredRow === item.id && !isMobile ? '#7c3aed' : '#1f2937',
                        transition: 'color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '6px' : '8px',
                        wordBreak: 'break-word'
                      }}>
                        {item.project_name}
                        {hoveredRow === item.id && !isMobile && (
                          <ExternalLink style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#7c3aed', flexShrink: 0 }} />
                        )}
                      </h4>
                    </div>

                    {/* 时间信息 */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', 
                      gap: isMobile ? '10px' : '16px'
                    }}>
                      <div style={{
                        padding: isMobile ? '10px 12px' : '14px 16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '10px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', marginBottom: isMobile ? '4px' : '6px' }}>
                          <Calendar style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: '#6b7280', flexShrink: 0 }} />
                          <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>发布时间</span>
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, color: '#1f2937', wordBreak: 'break-word' }}>
                          {formatDateTime(item.publish_time)}
                        </div>
                      </div>
                      
                      <div style={{
                        padding: isMobile ? '10px 12px' : '14px 16px',
                        backgroundColor: status.status === 'ended' ? '#fef2f2' : status.status === 'urgent' ? '#fffbeb' : '#f8fafc',
                        borderRadius: '10px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', marginBottom: isMobile ? '4px' : '6px' }}>
                          <Clock style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: status.color, flexShrink: 0 }} />
                          <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>开标时间</span>
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, color: status.color, wordBreak: 'break-word' }}>
                          {formatDateTime(item.open_bid_time)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: isMobile ? '40px 20px' : '80px 0', textAlign: 'center' }}>
              <div style={{ 
                width: isMobile ? '80px' : '100px', 
                height: isMobile ? '80px' : '100px', 
                margin: '0 auto 24px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FolderOpen style={{ width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', color: '#9ca3af' }} />
              </div>
              <p style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px', marginBottom: '8px' }}>暂无招标项目</p>
              <p style={{ color: '#9ca3af', fontSize: isMobile ? '12px' : '14px' }}>您发布的招标项目将显示在这里</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

